import React, { useState, useRef, useEffect } from 'react';
import { Heart, Sun, Cloud, Zap, Shield, Star, ChevronRight, RefreshCw, Globe, Share2, Download } from 'lucide-react';
import verses from './verseDB';

const BibleVerseApp = () => {
  const [currentStep, setCurrentStep] = useState('input');
  const [selectedFeeling, setSelectedFeeling] = useState('');
  const [verse, setVerse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [language, setLanguage] = useState('pt');
  const [showShare, setShowShare] = useState(false);
  const shareCanvasRef = useRef(null);

  const translations = {
    en: {
      title: "Find Your",
      titleHighlight: "Verse",
      subtitle: "Discover a Bible verse tailored to how you're feeling today",
      question: "How are you feeling today?",
      getVerse: "Get My Verse",
      anotherVerse: "Another Verse",
      startOver: "Start Over",
      share: "Share Verse",
      shareTitle: "Share This Verse",
      shareDescription: "Share this beautiful verse with your friends and family",
      downloadImage: "Download Image",
      copyLink: "Copy Text",
      verseFor: "Your verse for feeling",
      messageTitle: "A Message for You",
      findingVerse: "Finding the perfect verse for you...",
      feelings: {
        Happy: "Happy",
        Sad: "Sad", 
        Nervous: "Nervous",
        Anxious: "Anxious",
        Peaceful: "Peaceful",
        Grateful: "Grateful"
      }
    },
    pt: {
      title: "Encontre Seu",
      titleHighlight: "Versículo",
      subtitle: "Descubra um versículo bíblico personalizado para como você está se sentindo hoje",
      question: "Como você está se sentindo hoje?",
      getVerse: "Obter Meu Versículo",
      anotherVerse: "Outro Versículo",
      startOver: "Recomeçar",
      share: "Compartilhar Versículo",
      shareTitle: "Compartilhar Este Versículo",
      shareDescription: "Compartilhe este lindo versículo com seus amigos e família",
      downloadImage: "Baixar Imagem",
      copyLink: "Copiar Texto",
      verseFor: "Seu versículo para quando se sente",
      messageTitle: "Uma Mensagem para Você",
      findingVerse: "Encontrando o versículo perfeito para você...",
      feelings: {
        Happy: "Feliz",
        Sad: "Triste",
        Nervous: "Nervoso(a)",
        Anxious: "Ansioso(a)",
        Peaceful: "Em Paz",
        Grateful: "Grato(a)"
      }
    }
  };

  const t = translations[language];

  const feelings = [
    { name: 'Happy', icon: Sun, color: 'from-yellow-400 to-orange-400', bg: 'bg-yellow-50' },
    { name: 'Sad', icon: Cloud, color: 'from-blue-400 to-indigo-400', bg: 'bg-blue-50' },
    { name: 'Nervous', icon: Zap, color: 'from-purple-400 to-pink-400', bg: 'bg-purple-50' },
    { name: 'Anxious', icon: Heart, color: 'from-red-400 to-pink-400', bg: 'bg-red-50' },
    { name: 'Peaceful', icon: Shield, color: 'from-green-400 to-teal-400', bg: 'bg-green-50' },
    { name: 'Grateful', icon: Star, color: 'from-amber-400 to-yellow-400', bg: 'bg-amber-50' }
  ];

  const verseDatabase = verses;

  const getVerse = async () => {
    setLoading(true);
    
    await new Promise(resolve => setTimeout(resolve, 1200));
    
    const feelingKey = selectedFeeling.toLowerCase();
    const verses = verseDatabase[language][feelingKey] || verseDatabase[language].peaceful;
    const randomVerse = verses[Math.floor(Math.random() * verses.length)];
    
    setVerse(randomVerse);
    setCurrentStep('result');
    setLoading(false);
  };

  const reset = () => {
    setCurrentStep('input');
    setSelectedFeeling('');
    setVerse(null);
    setLoading(false);
    setShowShare(false);
  };

  const selectedFeelingData = feelings.find(f => f.name === selectedFeeling);

  const drawIcon = (ctx, iconName, x, y, size, color = 'white') => {
    ctx.strokeStyle = color;
    ctx.fillStyle = color;
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    const halfSize = size / 2;
    
    switch (iconName) {
      case 'Sun': // Happy
        // Draw sun rays
        const rayLength = halfSize * 0.4;
        const sunInnerRadius = halfSize * 0.6;
        for (let i = 0; i < 8; i++) {
          const angle = (i * Math.PI) / 4;
          const startX = x + Math.cos(angle) * sunInnerRadius;
          const startY = y + Math.sin(angle) * sunInnerRadius;
          const endX = x + Math.cos(angle) * (sunInnerRadius + rayLength);
          const endY = y + Math.sin(angle) * (sunInnerRadius + rayLength);
          
          ctx.beginPath();
          ctx.moveTo(startX, startY);
          ctx.lineTo(endX, endY);
          ctx.stroke();
        }
        // Draw sun center
        ctx.beginPath();
        ctx.arc(x, y, halfSize * 0.5, 0, 2 * Math.PI);
        ctx.fill();
        break;

      case 'Cloud': // Sad
        ctx.beginPath();
        // Main cloud body
        ctx.arc(x - halfSize * 0.3, y, halfSize * 0.4, 0, 2 * Math.PI);
        ctx.arc(x, y - halfSize * 0.2, halfSize * 0.5, 0, 2 * Math.PI);
        ctx.arc(x + halfSize * 0.3, y, halfSize * 0.4, 0, 2 * Math.PI);
        ctx.arc(x, y + halfSize * 0.1, halfSize * 0.6, 0, 2 * Math.PI);
        ctx.fill();
        break;

      case 'Zap': // Nervous
        ctx.beginPath();
        ctx.moveTo(x - halfSize * 0.2, y - halfSize * 0.6);
        ctx.lineTo(x + halfSize * 0.1, y - halfSize * 0.1);
        ctx.lineTo(x - halfSize * 0.1, y - halfSize * 0.1);
        ctx.lineTo(x + halfSize * 0.2, y + halfSize * 0.6);
        ctx.lineTo(x - halfSize * 0.1, y + halfSize * 0.1);
        ctx.lineTo(x + halfSize * 0.1, y + halfSize * 0.1);
        ctx.closePath();
        ctx.fill();
        break;

      case 'Heart': // Anxious
        ctx.beginPath();
        const heartWidth = halfSize * 0.8;
        const heartHeight = halfSize * 0.7;
        // Left heart curve
        ctx.arc(x - heartWidth * 0.25, y - heartHeight * 0.25, heartWidth * 0.25, 0, 2 * Math.PI);
        // Right heart curve  
        ctx.arc(x + heartWidth * 0.25, y - heartHeight * 0.25, heartWidth * 0.25, 0, 2 * Math.PI);
        ctx.fill();
        // Heart bottom point
        ctx.beginPath();
        ctx.moveTo(x - halfSize * 0.5, y);
        ctx.lineTo(x, y + halfSize * 0.6);
        ctx.lineTo(x + halfSize * 0.5, y);
        ctx.closePath();
        ctx.fill();
        break;

      case 'Shield': // Peaceful
        ctx.beginPath();
        ctx.moveTo(x, y - halfSize * 0.7);
        ctx.lineTo(x + halfSize * 0.5, y - halfSize * 0.3);
        ctx.lineTo(x + halfSize * 0.5, y + halfSize * 0.2);
        ctx.quadraticCurveTo(x + halfSize * 0.5, y + halfSize * 0.7, x, y + halfSize * 0.7);
        ctx.quadraticCurveTo(x - halfSize * 0.5, y + halfSize * 0.7, x - halfSize * 0.5, y + halfSize * 0.2);
        ctx.lineTo(x - halfSize * 0.5, y - halfSize * 0.3);
        ctx.closePath();
        ctx.fill();
        // Shield check mark
        ctx.strokeStyle = color === 'white' ? '#10B981' : 'white';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(x - halfSize * 0.2, y);
        ctx.lineTo(x - halfSize * 0.05, y + halfSize * 0.15);
        ctx.lineTo(x + halfSize * 0.25, y - halfSize * 0.1);
        ctx.stroke();
        break;

      case 'Star': // Grateful
        ctx.beginPath();
        const spikes = 5;
        const starOuterRadius = halfSize * 0.6;
        const starInnerRadius = halfSize * 0.3;
        
        for (let i = 0; i < spikes * 2; i++) {
          const angle = (i * Math.PI) / spikes;
          const radius = i % 2 === 0 ? starOuterRadius : starInnerRadius;
          const pointX = x + Math.cos(angle - Math.PI / 2) * radius;
          const pointY = y + Math.sin(angle - Math.PI / 2) * radius;
          
          if (i === 0) {
            ctx.moveTo(pointX, pointY);
          } else {
            ctx.lineTo(pointX, pointY);
          }
        }
        ctx.closePath();
        ctx.fill();
        break;

      default:
        // Fallback - simple circle
        ctx.beginPath();
        ctx.arc(x, y, halfSize * 0.5, 0, 2 * Math.PI);
        ctx.fill();
        break;
    }
  };

  const generateShareImage = () => {
    const canvas = shareCanvasRef.current;
    if (!canvas || !verse || !selectedFeelingData) return null;

    try {
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;

      const width = 800;
      const addV = verse.text.length > 49 ? Math.floor(verse.text.length / 49) * 40 : 0;
      const addM = verse.message.length > 100 ? Math.floor(verse.message.length / 100) * 20 : 0;
      const height = 750 + addV + addM; // Taller for more content
      
      canvas.width = width;
      canvas.height = height;

      // Background gradient matching app
      const gradient = ctx.createLinearGradient(0, 0, width, height);
      gradient.addColorStop(0, '#f8fafc'); // slate-50
      gradient.addColorStop(1, '#e2e8f0'); // slate-200
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);

      // Main white card (like the app)
      ctx.fillStyle = 'white';
      ctx.shadowColor = 'rgba(0, 0, 0, 0.1)';
      ctx.shadowBlur = 20;
      ctx.shadowOffsetY = 10;
      
      const cardX = 40;
      const cardY = 60;
      const cardWidth = width - 80;
      const cardHeight = height - 120;
      const radius = 24;
      
      // Rounded rectangle for card
      ctx.beginPath();
      ctx.moveTo(cardX + radius, cardY);
      ctx.lineTo(cardX + cardWidth - radius, cardY);
      ctx.quadraticCurveTo(cardX + cardWidth, cardY, cardX + cardWidth, cardY + radius);
      ctx.lineTo(cardX + cardWidth, cardY + cardHeight - radius);
      ctx.quadraticCurveTo(cardX + cardWidth, cardY + cardHeight, cardX + cardWidth - radius, cardY + cardHeight);
      ctx.lineTo(cardX + radius, cardY + cardHeight);
      ctx.quadraticCurveTo(cardX, cardY + cardHeight, cardX, cardY + cardHeight - radius);
      ctx.lineTo(cardX, cardY + radius);
      ctx.quadraticCurveTo(cardX, cardY, cardX + radius, cardY);
      ctx.closePath();
      ctx.fill();

      // Reset shadow
      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;
      ctx.shadowOffsetY = 0;

      // Emotion icon circle (matching the app)
      const iconSize = 80;
      const iconX = width / 2;
      const iconY = 160;
      
      const emotionGradient = ctx.createLinearGradient(iconX - iconSize/2, iconY - iconSize/2, iconX + iconSize/2, iconY + iconSize/2);
      const colors = selectedFeelingData.color.includes('yellow') ? ['#FBBF24', '#F59E0B'] :
                     selectedFeelingData.color.includes('blue') ? ['#60A5FA', '#3B82F6'] :
                     selectedFeelingData.color.includes('purple') ? ['#A855F7', '#9333EA'] :
                     selectedFeelingData.color.includes('red') ? ['#F87171', '#EF4444'] :
                     selectedFeelingData.color.includes('green') ? ['#4ADE80', '#10B981'] :
                     ['#FBBF24', '#F59E0B'];
      
      emotionGradient.addColorStop(0, colors[0]);
      emotionGradient.addColorStop(1, colors[1]);
      
      ctx.fillStyle = emotionGradient;
      ctx.beginPath();
      ctx.arc(iconX, iconY, iconSize / 2, 0, 2 * Math.PI);
      ctx.fill();

      // Draw the icon inside the circle
      drawIcon(ctx, selectedFeelingData.icon.name, iconX, iconY, iconSize * 0.6, 'white');

      // Small text above verse
      ctx.fillStyle = '#94a3b8'; // slate-400
      ctx.font = 'bold 14px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
      ctx.textAlign = 'center';
      const feelingText = language === 'en' ? 
        `YOUR VERSE FOR FEELING ${selectedFeeling.toUpperCase()}` : 
        `SEU VERSÍCULO PARA QUANDO SE SENTE ${t.feelings[selectedFeeling]?.toUpperCase()}`;
      ctx.fillText(feelingText, width / 2, 250);

      // Main verse text (like the app)
      ctx.fillStyle = '#1e293b'; // slate-800
      ctx.font = 'italic 28px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
      ctx.textAlign = 'center';
      const maxWidth = width - 120;
      const verseLines = wrapText(ctx, `"${verse.text}"`, maxWidth);
      const lineHeight = 40;
      let currentY = 300;

      verseLines.forEach(line => {
        ctx.fillText(line, width / 2, currentY);
        currentY += lineHeight;
      });

      // Reference
      ctx.font = 'bold italic 22px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
      ctx.fillStyle = '#64748b'; // slate-500
      ctx.fillText(`— ${verse.reference}`, width / 2, currentY + 30);
      currentY += 80;

      // Motivational message box (matching the app design)
      const messageBoxY = currentY;
      const messageBoxHeight = 200;
      
      // Gradient background for message box
      const messageGradient = ctx.createLinearGradient(cardX, messageBoxY, cardX + cardWidth, messageBoxY + messageBoxHeight);
      messageGradient.addColorStop(0, colors[0]);
      messageGradient.addColorStop(1, colors[1]);
      
      ctx.fillStyle = messageGradient;
      const msgRadius = 16;
      ctx.beginPath();
      ctx.moveTo(cardX + 20 + msgRadius, messageBoxY);
      ctx.lineTo(cardX + cardWidth - 20 - msgRadius, messageBoxY);
      ctx.quadraticCurveTo(cardX + cardWidth - 20, messageBoxY, cardX + cardWidth - 20, messageBoxY + msgRadius);
      ctx.lineTo(cardX + cardWidth - 20, messageBoxY + messageBoxHeight - msgRadius);
      ctx.quadraticCurveTo(cardX + cardWidth - 20, messageBoxY + messageBoxHeight, cardX + cardWidth - 20 - msgRadius, messageBoxY + messageBoxHeight);
      ctx.lineTo(cardX + 20 + msgRadius, messageBoxY + messageBoxHeight);
      ctx.quadraticCurveTo(cardX + 20, messageBoxY + messageBoxHeight, cardX + 20, messageBoxY + messageBoxHeight - msgRadius);
      ctx.lineTo(cardX + 20, messageBoxY + msgRadius);
      ctx.quadraticCurveTo(cardX + 20, messageBoxY, cardX + 20 + msgRadius, messageBoxY);
      ctx.closePath();
      ctx.fill();

      // Inner white box for message
      ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
      const innerRadius = 12;
      const innerY = messageBoxY + 15;
      const innerHeight = messageBoxHeight - 30;
      ctx.beginPath();
      ctx.moveTo(cardX + 35 + innerRadius, innerY);
      ctx.lineTo(cardX + cardWidth - 35 - innerRadius, innerY);
      ctx.quadraticCurveTo(cardX + cardWidth - 35, innerY, cardX + cardWidth - 35, innerY + innerRadius);
      ctx.lineTo(cardX + cardWidth - 35, innerY + innerHeight - innerRadius);
      ctx.quadraticCurveTo(cardX + cardWidth - 35, innerY + innerHeight, cardX + cardWidth - 35 - innerRadius, innerY + innerHeight);
      ctx.lineTo(cardX + 35 + innerRadius, innerY + innerHeight);
      ctx.quadraticCurveTo(cardX + 35, innerY + innerHeight, cardX + 35, innerY + innerHeight - innerRadius);
      ctx.lineTo(cardX + 35, innerY + innerRadius);
      ctx.quadraticCurveTo(cardX + 35, innerY, cardX + 35 + innerRadius, innerY);
      ctx.closePath();
      ctx.fill();

      // Message title
      ctx.fillStyle = '#1e293b';
      ctx.font = 'bold 18px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
      ctx.fillText(t.messageTitle, width / 2, innerY + 35);

      // Message content
      ctx.font = '16px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
      ctx.fillStyle = '#374151'; // gray-700
      const messageLines = wrapText(ctx, verse.message, width - 200);
      let messageY = innerY + 65;
      
      messageLines.slice(0, 5).forEach(line => { // Limit to 5 lines
        ctx.fillText(line, width / 2, messageY);
        messageY += 22;
      });

      // App branding at bottom
      ctx.fillStyle = colors[1];
      ctx.font = 'bold 16px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
      ctx.fillText('www.godtellsyou.com.br', width / 2, height - 40);

      return canvas.toDataURL('image/png');
    } catch (error) {
      console.error('Error generating image:', error);
      return null;
    }
  };

  const wrapText = (ctx, text, maxWidth) => {
    const words = text.split(' ');
    const lines = [];
    let currentLine = words[0];

    for (let i = 1; i < words.length; i++) {
      const word = words[i];
      const width = ctx.measureText(currentLine + ' ' + word).width;
      if (width < maxWidth) {
        currentLine += ' ' + word;
      } else {
        lines.push(currentLine);
        currentLine = word;
      }
    }
    lines.push(currentLine);
    return lines;
  };

  const handleShare = async () => {
    setShowShare(true);
    
    setTimeout(() => {
      const imageDataUrl = generateShareImage();
      
      if (!imageDataUrl) {
        alert(language === 'en' ? 'Error generating image' : 'Erro ao gerar imagem');
        return;
      }

      if (navigator.share) {
        fetch(imageDataUrl)
          .then(response => response.blob())
          .then(blob => {
            const file = new File([blob], 'bible-verse.png', { type: 'image/png' });
            return navigator.share({
              title: verse.reference,
              text: verse.text,
              files: [file]
            });
          })
          .catch(() => {
            // Modal is already open for manual sharing
          });
      }
    }, 200);
  };

  const downloadImage = () => {
    const imageDataUrl = generateShareImage();
    if (!imageDataUrl) return;

    const link = document.createElement('a');
    link.download = `bible-verse-${selectedFeeling.toLowerCase()}.png`;
    link.href = imageDataUrl;
    link.click();
  };

  const copyToClipboard = async () => {
    const text = `${verse.text} — ${verse.reference}`;
    try {
      await navigator.clipboard.writeText(text);
      alert(language === 'en' ? 'Verse copied to clipboard!' : 'Versículo copiado para a área de transferência!');
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  };

  const LanguageToggle = () => (
    <div className="fixed top-6 right-6 z-50">
      <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-200 p-2 flex items-center gap-2">
        <Globe className="w-4 h-4 text-slate-600" />
        <button
          onClick={() => setLanguage(language === 'en' ? 'pt' : 'en')}
          className="flex items-center gap-2 px-3 py-2 rounded-xl font-medium text-sm transition-all duration-200 hover:bg-slate-100"
        >
          <span className={language === 'en' ? 'text-slate-800' : 'text-slate-500'}>EN</span>
          <span className="text-slate-300">|</span>
          <span className={language === 'pt' ? 'text-slate-800' : 'text-slate-500'}>PT</span>
        </button>
      </div>
    </div>
  );

  const ShareModal = () => {
    if (!showShare) return null;

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full p-8">
          <div className="text-center mb-6">
            <h3 className="text-2xl font-bold text-slate-800 mb-2">{t.shareTitle}</h3>
            <p className="text-slate-600">{t.shareDescription}</p>
          </div>

          <div className="mb-6">
            <canvas 
              ref={shareCanvasRef}
              className="w-full h-auto rounded-2xl shadow-lg border-2 border-slate-100"
              style={{ maxHeight: '300px' }}
            />
          </div>

          <div className="flex flex-col gap-3">
            <button
              onClick={downloadImage}
              className="flex items-center justify-center gap-2 px-6 py-3 bg-slate-800 text-white rounded-2xl font-semibold hover:bg-slate-700 transition-all duration-200"
            >
              <Download className="w-5 h-5" />
              {t.downloadImage}
            </button>
            <button
              onClick={copyToClipboard}
              className="flex items-center justify-center gap-2 px-6 py-3 border-2 border-slate-200 text-slate-600 rounded-2xl font-semibold hover:border-slate-300 hover:bg-slate-50 transition-all duration-200"
            >
              <Share2 className="w-5 h-5" />
              {t.copyLink}
            </button>
            <button
              onClick={() => setShowShare(false)}
              className="px-6 py-3 text-slate-500 hover:text-slate-700 font-medium transition-all duration-200"
            >
              {language === 'en' ? 'Close' : 'Fechar'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        {/*<LanguageToggle />*/}
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-6 relative">
            <div className="absolute inset-0 rounded-full border-4 border-slate-200"></div>
            <div className="absolute inset-0 rounded-full border-4 border-slate-600 border-t-transparent animate-spin"></div>
          </div>
          <p className="text-slate-600 text-lg font-medium">{t.findingVerse}</p>
        </div>
      </div>
    );
  }

  if (currentStep === 'result' && verse) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        {/*<LanguageToggle />*/}
        <div className="max-w-2xl w-full">
          <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12 text-center">
            <div className={`w-20 h-20 mx-auto mb-8 rounded-full bg-gradient-to-r ${selectedFeelingData?.color} flex items-center justify-center`}>
              {selectedFeelingData && <selectedFeelingData.icon className="w-10 h-10 text-white" />}
            </div>
            
            <div className="mb-8">
              <p className="text-slate-500 text-sm uppercase tracking-wider mb-6">{t.verseFor} {t.feelings[selectedFeeling]?.toLowerCase()}</p>
              <blockquote className="text-2xl md:text-3xl font-light text-slate-800 leading-relaxed mb-6 italic">
                "{verse.text}"
              </blockquote>
              <cite className="text-lg text-slate-600 font-medium mb-8 block">— {verse.reference}</cite>
              
              <div className={`bg-gradient-to-r ${selectedFeelingData?.color} p-6 rounded-2xl mb-8 shadow-lg`}>
                <div className="bg-white/95 backdrop-blur-sm rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2 justify-center">
                    <Heart className="w-5 h-5 text-slate-600" />
                    {t.messageTitle}
                  </h3>
                  <p className="text-slate-700 leading-relaxed text-base">
                    {verse.message}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={getVerse}
                className="flex items-center justify-center gap-2 px-8 py-4 bg-slate-800 text-white rounded-2xl font-semibold hover:bg-slate-700 transition-all duration-200 transform hover:scale-105"
              >
                <RefreshCw className="w-5 h-5" />
                {t.anotherVerse}
              </button>
              <button
                onClick={handleShare}
                className="flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-2xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-200 transform hover:scale-105"
              >
                <Share2 className="w-5 h-5" />
                {t.share}
              </button>
              <button
                onClick={reset}
                className="px-8 py-4 border-2 border-slate-200 text-slate-600 rounded-2xl font-semibold hover:border-slate-300 hover:bg-slate-50 transition-all duration-200"
              >
                {t.startOver}
              </button>
            </div>
          </div>
        </div>
        <ShareModal />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      {/*<LanguageToggle />*/}
      <div className="max-w-4xl w-full">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-slate-800 mb-4">
            {t.title} <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">{t.titleHighlight}</span>
          </h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed">
            {t.subtitle}
          </p>
        </div>

        <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12">
          <div className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-800 mb-6 text-center">
              {t.question}
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {feelings.map((feeling) => (
                <button
                  key={feeling.name}
                  onClick={() => setSelectedFeeling(feeling.name)}
                  className={`relative p-6 rounded-2xl border-2 transition-all duration-300 transform hover:scale-105 ${
                    selectedFeeling === feeling.name
                      ? `border-transparent bg-gradient-to-r ${feeling.color} text-white shadow-lg`
                      : `border-slate-200 hover:border-slate-300 ${feeling.bg} hover:shadow-md`
                  }`}
                >
                  <div className="flex flex-col items-center">
                    <feeling.icon className={`w-8 h-8 mb-3 ${
                      selectedFeeling === feeling.name ? 'text-white' : 'text-slate-600'
                    }`} />
                    <span className={`font-semibold text-lg ${
                      selectedFeeling === feeling.name ? 'text-white' : 'text-slate-700'
                    }`}>
                      {t.feelings[feeling.name]}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {selectedFeeling && (
            <div className="text-center">
              <button
                onClick={getVerse}
                className="inline-flex items-center gap-3 px-12 py-4 bg-gradient-to-r from-slate-800 to-slate-700 text-white rounded-2xl font-bold text-lg hover:from-slate-700 hover:to-slate-600 transition-all duration-200 transform hover:scale-105 shadow-lg"
              >
                {t.getVerse}
                <ChevronRight className="w-6 h-6" />
              </button>
            </div>
          )}
        </div>
      </div>
      <ShareModal />
    </div>
  );
};

export default BibleVerseApp;